"use client";

import { SegmentedToggle } from "@/components/segmentedToggle/segmentedToggle.component";
import { Text } from "@/components/text/text.component";

import {
  COMPARE_PLAN_TABLE_DEFAULTS,
  COMPARE_PLAN_TABLE_SCROLL_MARGIN_TOP_PX,
} from "@/sanity/defaults/comparePlanTable.defaults";

import { ComparePlanTableCategories } from "./comparePlanTableCategories.component";
import { ComparePlanTableColumnHeaders } from "./comparePlanTableColumnHeaders.component";
import { ComparePlanTableFootnotes } from "./comparePlanTableFootnotes.component";
import {
  type SanityComparePlanTableQueryResult,
  useComparePlanTableSectionState,
} from "./useComparePlanTableSectionState";

export const ComparePlanTableSection = ({
  block,
}: ComparePlanTableSectionProps) => {
  if (!block?.title) return null;

  const { filterOptions, filter, setFilter, activeView } =
    useComparePlanTableSectionState(block);

  const columns = activeView?.columns ?? [];
  const categories = activeView?.categories ?? [];
  const gridTemplateColumns = columns.length
    ? `250px repeat(${columns.length}, minmax(250px, 1fr))`
    : "";
  const footnotes = activeView?.footnotes ?? [];
  const sectionId = block.sectionId ?? COMPARE_PLAN_TABLE_DEFAULTS.sectionId;

  return (
    <section
      role="table"
      aria-label="Compare all plan features"
      id={sectionId}
      className="container my-10 w-full max-w-[1376px] bg-white px-0 md:my-16 md:px-9"
      style={{ scrollMarginTop: COMPARE_PLAN_TABLE_SCROLL_MARGIN_TOP_PX }}
      data-testid="compare-plan-table-section">
      <div className="mx-5 flex flex-col items-center gap-10 text-center md:mx-0">
        <Text variant="h3" component="h3">
          {block.title}
        </Text>

        {filterOptions.length > 1 ? (
          <div className="mx-auto w-full max-w-xl">
            <SegmentedToggle
              ariaLabel="With cameras or without cameras"
              value={filter}
              onChange={setFilter}
              className="border-primary-400 border-2 bg-white shadow-sm"
              inactiveLabelClassName="!bg-white !text-greyscale-body"
              options={filterOptions}
            />
          </div>
        ) : null}
      </div>

      {columns.length ? (
        <ComparePlanTableColumnHeaders
          columns={columns}
          gridTemplateColumns={gridTemplateColumns}
        />
      ) : null}

      {categories.length && gridTemplateColumns ? (
        <ComparePlanTableCategories
          categories={categories}
          gridTemplateColumns={gridTemplateColumns}
          columnCount={columns.length}
        />
      ) : null}

      <ComparePlanTableFootnotes footnotes={footnotes} />
    </section>
  );
};

type ComparePlanTableSectionProps = {
  block: SanityComparePlanTableQueryResult;
};
